import { Button, Form, message, Space } from 'antd';
import axios from 'axios';
import React, { useCallback, useEffect, useState } from 'react';

import type {
  CleanKindFetchResponse,
  CleanKindResult,
  SubscriptionPlanData,
  TicketsForCleanKind,
} from '../../sharedTypes';
import CleanKindInformation from '../CleanKindInformation';
import CleanKindItems from '../CleanKindItems';
import { initFreeItem, initPaidItem } from '../constants';
import type {
  FreeItemTableData,
  InformationFormDataType,
  PaidItemTableData,
} from '../types';
import {
  excludeDummyItems,
  fetchDataWithNewCleanKindId,
  formatAddCleanKindRequestData,
  formatEditCleanKindRequestData,
  initInformationFormData,
} from './helper';
import styles from './index.module.scss';

type CleanKindEditorProps = {
  type: 'add' | 'edit';
  closeModal: () => void;
  getLatestData: () => void;
  selectOptions: {
    subscriptionPlanList: SubscriptionPlanData[] | undefined;
    tickets: TicketsForCleanKind[] | undefined;
  };
  cleanKindData: CleanKindFetchResponse | undefined;
  record?: CleanKindResult;
};

const { useForm } = Form;

const CleanKindEditor = ({
  type,
  closeModal,
  getLatestData,
  selectOptions,
  cleanKindData,
  record,
}: CleanKindEditorProps) => {
  const { subscriptionPlanList, tickets } = selectOptions;
  const cleanKindId = record?.cleanKindId;

  const [form] = useForm<InformationFormDataType>();

  const [freeItem, setFreeItem] = useState<FreeItemTableData[]>([initFreeItem]);
  const [paidItems, setPaidItems] = useState<PaidItemTableData[]>([
    initPaidItem,
  ]);
  const [isValidating, setIsValidating] = useState(false);
  const [isAllCleanItemsValidated, setIsAllCleanItemsValidated] =
    useState(false);

  const addCleanKind = useCallback(async () => {
    const formValues = form.getFieldsValue();
    const data = formatAddCleanKindRequestData(formValues, {
      freeItem,
      paidItems,
    });
    try {
      await axios({
        method: 'POST',
        url: '/ezcon/api/addCleanKind',
        headers: {
          authorization: localStorage.getItem('feathers-jwt'),
        },
        data,
      });
      message.success('新增清潔類別成功!');
      getLatestData();
      closeModal();
    } catch (error) {
      message.error('新增清潔類別失敗!');
    }
  }, [closeModal, form, freeItem, getLatestData, paidItems]);

  const editCleanKind = useCallback(async () => {
    const formValues = form.getFieldsValue();
    let data = formatEditCleanKindRequestData({
      formValues,
      cleanKindItems: { freeItem, paidItems },
      cleanKindId,
    });

    const cleanServiceItemList = excludeDummyItems(data.cleanServiceItemList);
    data = {
      ...data,
      cleanServiceItemList,
    };

    try {
      await axios({
        method: 'PUT',
        url: '/ezcon/api/editCleanKind',
        headers: {
          authorization: localStorage.getItem('feathers-jwt'),
        },
        data,
      });
      message.success('編輯清潔類別成功!');
      getLatestData();
      closeModal();
    } catch (error) {
      message.error('編輯清潔類別失敗!');
    }
  }, [cleanKindId, closeModal, form, freeItem, getLatestData, paidItems]);

  useEffect(() => {
    if (type === 'add') {
      form.setFieldsValue(initInformationFormData);
    }
  }, [form, type]);

  useEffect(() => {
    if (type === 'edit') {
      fetchDataWithNewCleanKindId(cleanKindId);
    }
  }, [cleanKindId, type]);

  const setFreeItemAndPaidItems = (cleanKindData: CleanKindFetchResponse) => {
    const freeItemData = cleanKindData.cleanServiceItemList.filter(
      (item) => item.priceInfo.kind === 'Free'
    );
    const freeItem = freeItemData.map((item) => ({
      ...item,
      itemName: item.name,
      ticketId:
        item.priceInfo.kind === 'Free'
          ? item.priceInfo.availableTicketId
          : [''],
    }));
    setFreeItem(freeItemData.length > 0 ? freeItem : [initFreeItem]);

    const paidItemsData = cleanKindData.cleanServiceItemList.filter(
      (item) => item.priceInfo.kind !== 'Free'
    );
    const paidItemList = paidItemsData.map((item) => {
      const { _id, name, priceInfo } = item;
      return {
        ...item,
        itemId: _id,
        itemName: name,
        taiwanPayPrice:
          priceInfo.kind === 'Both' || priceInfo.kind === 'TaiwanPay'
            ? priceInfo.taiwanPayPrice
            : null,
        pointPrice:
          priceInfo.kind === 'Both' || priceInfo.kind === 'Point'
            ? priceInfo.pointPrice
            : null,
        reservationLimits: {
          reservationLowerLimit:
            priceInfo.kind !== 'Free' ? priceInfo.reservationLowerLimit : null,
          reservationUpperLimit:
            priceInfo.kind !== 'Free' ? priceInfo.reservationUpperLimit : null,
        },
      };
    });
    setPaidItems(paidItemsData.length > 0 ? paidItemList : [initPaidItem]);
  };

  useEffect(() => {
    if (cleanKindData) {
      setFreeItemAndPaidItems(cleanKindData);
    }
  }, [cleanKindData]);

  useEffect(() => {
    if (isAllCleanItemsValidated && type === 'add') {
      setIsAllCleanItemsValidated(false);
      addCleanKind();
    }
    if (isAllCleanItemsValidated && type === 'edit') {
      setIsAllCleanItemsValidated(false);
      editCleanKind();
    }
  }, [addCleanKind, editCleanKind, isAllCleanItemsValidated, type]);

  const planOptions = subscriptionPlanList?.map((plan) => ({
    label: plan.planName,
    value: plan.subscriptionPlanId,
  }));

  const ticketOptions = tickets?.map((ticket) => ({
    label: ticket.ticketName,
    value: ticket.ticketsId,
  }));

  const handleFormValuesChange = (changedValues: InformationFormDataType) => {
    if (
      Object.prototype.hasOwnProperty.call(changedValues, 'janitor') &&
      changedValues.janitor !== 'Partner'
    ) {
      form.setFieldValue('partnerName', null);
    }

    if (
      Object.prototype.hasOwnProperty.call(
        changedValues,
        'reservationLimitRule'
      ) &&
      changedValues.reservationLimitRule !== 'InTimeRange'
    ) {
      form.setFieldsValue({ limitRuleValue: null, limitRuleUnit: null });
    }
  };

  const updateIsAllCleanItemsValidated = (isAllItemsPassed: boolean) => {
    if (isAllItemsPassed) {
      setIsValidating(false);
    }
    setIsAllCleanItemsValidated(isAllItemsPassed);
  };

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        initialValues={initInformationFormData}
        onValuesChange={handleFormValuesChange}
      >
        <CleanKindInformation
          form={form}
          planOptions={planOptions}
          updatePicture={(newPicture) =>
            form.setFieldValue('picture', [newPicture])
          }
          type={type}
          cleanKindData={cleanKindData}
        />
      </Form>
      <CleanKindItems
        type={type}
        ticketOptions={ticketOptions}
        freeItem={freeItem}
        paidItems={paidItems}
        updateFreeItem={setFreeItem}
        updatePaidItems={setPaidItems}
        isValidating={isValidating}
        updateIsValidating={setIsValidating}
        updateIsAllCleanItemsValidated={updateIsAllCleanItemsValidated}
      />
      <Space className={styles.footerButtonsContainer}>
        <Button onClick={closeModal}>取消</Button>
        <Button
          onClick={async () => {
            setIsValidating(true);
            try {
              await form.validateFields();
            } catch (error) {
              return;
            }
          }}
          type="primary"
        >
          確定
        </Button>
      </Space>
    </>
  );
};

export default CleanKindEditor;
